import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, featuresAPI } from './api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await authAPI.getMe();
      setUser(response.data);

      // Fetch features
      const featuresResponse = await featuresAPI.getAvailable();
      setFeatures(featuresResponse.data);

      // Get hospital from stored data
      const storedHospital = localStorage.getItem('hospital');
      if (storedHospital) {
        setHospital(JSON.parse(storedHospital));
      }
    } catch (err) {
      console.error('Auth error:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('hospital');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login(email, password);
      const { token, user: userData, hospital: hospitalData } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      if (hospitalData) {
        localStorage.setItem('hospital', JSON.stringify(hospitalData));
        setHospital(hospitalData);
      }

      setUser(userData);

      // Fetch features after login
      const featuresResponse = await featuresAPI.getAvailable();
      setFeatures(featuresResponse.data);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const _hydrate = async (response) => {
    const { token, user: userData, hospital: hospitalData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (hospitalData) {
      localStorage.setItem('hospital', JSON.stringify(hospitalData));
      setHospital(hospitalData);
    }
    setUser(userData);
    try {
      const featuresResponse = await featuresAPI.getAvailable();
      setFeatures(featuresResponse.data);
    } catch { /* non-blocking */ }
    return { success: true };
  };

  const exchangeGoogleSession = async (sessionId) => {
    try {
      setError(null);
      const response = await authAPI.googleSession(sessionId);
      return await _hydrate(response);
    } catch (err) {
      const message = err.response?.data?.detail || 'Google sign-in failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('hospital');
    setUser(null);
    setHospital(null);
    setFeatures({});
  };

  const hasFeature = (featureName) => {
    if (user?.is_super_admin) return true;
    return features[featureName] === true;
  };

  // Phase-2 RBAC helper. Returns true iff `code` is in the logged-in user's role_permissions.
  // Super Admin always returns true. Use this in components to gate buttons, sidebar entries,
  // page sections, etc. — exactly the way the backend gates endpoints.
  const hasPermission = (code) => {
    if (!code) return false;
    if (user?.is_super_admin) return true;
    const perms = user?.role_permissions || [];
    return perms.includes(code);
  };

  // Accepts an array of permission codes; returns true if the user has ANY of them.
  const hasAnyPermission = (codes) => {
    if (!Array.isArray(codes) || codes.length === 0) return false;
    if (user?.is_super_admin) return true;
    const perms = user?.role_permissions || [];
    return codes.some(c => perms.includes(c));
  };

  const value = {
    user,
    hospital,
    features,
    loading,
    error,
    login,
    exchangeGoogleSession,
    logout,
    hasFeature,
    hasPermission,
    hasAnyPermission,
    refreshUser: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * v34 — Resolve the post-login landing path for a user.
 * Reads the role.default_landing field (which the backend exposes on /auth/me)
 * and falls back to /dashboard if unset OR if it points at a non-dashboard route
 * (defensive guard — every login must land on a dashboard per customer ask).
 * Used by all post-login navigations: LoginPage, LoginMethods, ChangePasswordPage,
 * AuthCallback, and the App.js root index route.
 */
const DASHBOARD_LANDING_PATHS = new Set([
  '/dashboard',
  '/doctor-dashboard',
  '/nurse-dashboard',
  '/patient-dashboard',
  '/notice-dashboard',
  '/vip-patients',
  '/platform-health',
  '/bed-status',
  '/finance-dashboard',
  '/stats360',
  '/staff-360',
  '/my-360',
]);

export const getLandingPath = (user) => {
  if (!user) return '/dashboard';
  const candidate = user.default_landing;
  if (candidate && DASHBOARD_LANDING_PATHS.has(candidate)) return candidate;
  // Role-aware sensible fallback for stale non-dashboard values from older configs.
  if (user.role_code === 'doctor') return '/doctor-dashboard';
  if (user.role_code === 'nurse') return '/nurse-dashboard';
  if (user.is_super_admin) return '/platform-health';
  return '/dashboard';
};

export default AuthContext;
